const BASE = 'https://api.proxy-checker.net/api/proxy-checker/';
const IPINFO = (ip) => `https://ipinfo.io/${ip}/json`;

const formatProxy = (dsn) => {
  const atMatch = dsn.match(/^([^@]+)@([^@]+)$/);
  if (atMatch) {
    const [, userpass, hostport] = atMatch;
    const [user, pass] = userpass.split(':');
    return `${hostport}:${user}:${pass}`;
  }
  return dsn;
};

const fetchIpInfo = async (ip) => {
  try {
    const res = await fetch(IPINFO(ip));
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
};

export const checkProxies = async (connectUrls) => {
  const formatted = connectUrls.map(formatProxy).join('\n');
  const body = new URLSearchParams();
  body.append('proxy_list', formatted);

  const response = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
    body: body.toString(),
  });

  const text = await response.text();
  let json;
  try { json = JSON.parse(text); }
  catch (_) { throw new Error(`Invalid JSON from API: ${text.slice(0, 300)}`); }

  if (!response.ok) throw new Error(json?.message || json?.error || `API Error ${response.status}`);
  return Array.isArray(json) ? json : json.data || json.proxies || json.result || [];
};

const ANON_LABEL = { 1: 'Transparent', 2: 'Anonymous', 3: 'Elite' };

const normalizeResult = async (raw) => {
  if (!raw) return { error: 'No result', ip: null };
  const alive = raw.valid === true;
  const data = raw.data || {};

  const speedSec =
    data.connection_time != null ? data.connection_time :
    data.timeout_ms != null ? data.timeout_ms / 1000 :
    data.response_time_ms != null ? data.response_time_ms / 1000 :
    data.speed_ms != null ? data.speed_ms / 1000 :
    data.timeout != null ? data.timeout :
    data.response_time != null ? data.response_time :
    data.speed != null ? data.speed : null;

  const anonRaw = data.anonymity_level ?? data.anonymity ?? null;
  const anonymity = anonRaw != null ? (ANON_LABEL[anonRaw] || `Level ${anonRaw}`) : null;

  const ip = raw.ip || null;

  let location = null;
  let org = null;
  let city = null;
  let region = null;
  let timezone = null;
  let postal = null;
  let loc = null; // Add loc for coordinates

  if (alive && ip) {
    const info = await fetchIpInfo(ip);
    if (info && !info.bogon) {
      location = {
        country_code: info.country || null,
        country_name: info.country || null,
      };
      city = info.city || null;
      region = info.region || null;
      org = info.org || null;
      timezone = info.timezone || null;
      postal = info.postal || null;
      loc = info.loc || null; // Parse loc
    }
  }

  return {
    ip,
    port: raw.port || null,
    protocol: raw.protocol || null,
    error: alive ? null : 'Dead',
    total_time: speedSec,
    anonymity,
    location,
    city,
    region,
    org,
    timezone,
    postal,
    loc, // Return loc
  };
};

export const runBatchCheck = async (
  allProxies, config, onProgress, onComplete, onError, signal
) => {
  const BATCH_SIZE = config.batchSize || 10;
  try {
    for (let i = 0; i < allProxies.length; i += BATCH_SIZE) {
      if (signal?.aborted) { onComplete(); return; }

      const batch = allProxies.slice(i, i + BATCH_SIZE);
      const batchIndices = batch.map((_, j) => i + j);

      try {
        const rawResults = await checkProxies(batch);
        const normalized = await Promise.all(
          rawResults.map((r) => normalizeResult(r))
        );
        batchIndices.forEach((originalIndex, batchIndex) => {
          onProgress(originalIndex, normalized[batchIndex]);
        });
      } catch (batchErr) {
        batchIndices.forEach((originalIndex) => {
          onProgress(originalIndex, { error: batchErr.message, ip: null });
        });
        const fatal =
          batchErr.message.includes('Failed to fetch') ||
          batchErr.message.includes('NetworkError') ||
          batchErr.message.includes('401') ||
          batchErr.message.includes('403');
        if (fatal) { onError(batchErr); return; }
      }

      if (i + BATCH_SIZE < allProxies.length && !signal?.aborted) {
        await new Promise((res) => setTimeout(res, 400));
      }
    }
    onComplete();
  } catch (err) {
    if (err.name === 'AbortError') onComplete();
    else onError(err);
  }
};